import React, { createContext, useContext, useEffect, useState } from 'react';
import { QuestionSpecies } from './Questions/QuestionSpecies';
import { QuestionName } from './Questions/QuestionName';
import { QuestionBreed } from './Questions/QuestionBreed';
import { QuestionAge } from './Questions/QuestionAge';
import { QuestionSize } from './Questions/QuestionSize';
import { QuestionResidence } from './Questions/QuestionResidence';
import { QuestionExercise } from './Questions/QuestionExercise';
import { QuestionImportance } from './Questions/QuestionImportance';
import { QuestionEmail } from './Questions/QuestionEmail';
import { Recommendation, RecommendationType } from './Recommendation/Recomendation';
import { QuestionSpendTime } from './Questions/QuestionSpendTime';
import useTenancy from '../../hooks/useTenancy';
import { useDebounce } from './Helpers/useDebounce';
import { quizIdentify } from './Helpers/quizIdentify';
import { quizQuestionPage } from '../../lib/analytics/events';

export enum Species {
  Cat = 'cat',
  Dog = 'dog',
}

export type ProductQuizContextType = {
  currentQuestionIndex: number;
  component: React.ReactNode;
  recommendation: React.ReactNode;
  quizAnswers: QuizAnswers;
  setQuizAnswers: React.Dispatch<React.SetStateAction<QuizAnswers>>;
  previousQuestion: () => void;
  nextQuestion: () => void;
  resetQuiz: () => void;
};

export type QuizAnswers = {
  species: Species | null;
  petName: string | null;
  breed: string | null;
  age: string | null;
  selectedSize: string | null;
  residence: string | null;
  spendTime: Set<string>;
  exercise: Set<string>;
  importance: Set<string>;
  email: string | null;
};

type QuizQuestion = {
  component: React.ReactNode;
};

const ProductQuizContext = createContext<ProductQuizContextType | undefined>(undefined);

const useSpeciesQuestions = (species: Species | null) => {
  const dogQuestions: QuizQuestion[] = [
    { component: <QuestionSpecies /> },
    { component: <QuestionName /> },
    { component: <QuestionBreed /> },
    { component: <QuestionAge /> },
    { component: <QuestionSize /> },
    { component: <QuestionResidence /> },
    { component: <QuestionExercise /> },
    { component: <QuestionImportance /> },
    { component: <QuestionEmail /> },
  ];

  const catQuestions: QuizQuestion[] = [
    { component: <QuestionSpecies /> },
    { component: <QuestionName /> },
    { component: <QuestionAge /> },
    { component: <QuestionSpendTime /> },
    { component: <QuestionImportance /> },
    { component: <QuestionEmail /> },
  ];

  return species === Species.Dog || species === null ? dogQuestions : catQuestions;
};

const useNextQuestion = (action: () => void) => {
  const nextQuestion = useDebounce(() => {
    window.requestAnimationFrame(() => {
      action();
      window.scrollTo(0, -1000);
    });
  }, 250);

  return nextQuestion;
};

const useDetermineRecommendation = (answers: QuizAnswers) => {
  const [recommendation, setRecommendation] = useState<React.ReactNode>(null);
  const tenancy = useTenancy();

  useEffect(() => {
    const completedEmailStep = answers.email !== null;

    if (!completedEmailStep) {
      setRecommendation(null);
      return;
    }

    quizIdentify(answers, tenancy);

    // Cats and small dogs -> Mini
    // All other dogs -> Collar
    if (answers.species === Species.Cat) {
      setRecommendation(<Recommendation recommendation={RecommendationType.Mini} />);
      quizQuestionPage.quizSuggestedDevice('mini');
      return;
    }

    if (answers.selectedSize === 'Extra Small' || answers.selectedSize === 'Small') {
      setRecommendation(<Recommendation recommendation={RecommendationType.Mini} />);
      quizQuestionPage.quizSuggestedDevice('mini');
      return;
    }

    setRecommendation(<Recommendation recommendation={RecommendationType.Collar} />);
    quizQuestionPage.quizSuggestedDevice('series_3_plus');
  }, [answers, tenancy]);

  return recommendation;
};

export const ProductQuizProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0);
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswers>({
    species: null,
    petName: null,
    breed: null,
    age: null,
    selectedSize: null,
    residence: null,
    spendTime: new Set(),
    exercise: new Set(),
    importance: new Set(),
    email: null,
  });

  const questions = useSpeciesQuestions(quizAnswers.species);

  const nextQuestion = useNextQuestion(() => setCurrentQuestionIndex((prev) => prev + 1));
  const previousQuestion = () => setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));

  const recommendation = useDetermineRecommendation(quizAnswers);

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setQuizAnswers({
      species: null,
      petName: null,
      breed: null,
      age: null,
      selectedSize: null,
      residence: null,
      spendTime: new Set(),
      exercise: new Set(),
      importance: new Set(),
      email: null,
    });
  };

  const value: ProductQuizContextType = {
    component: questions[currentQuestionIndex].component,
    recommendation: recommendation,
    currentQuestionIndex,
    quizAnswers,
    setQuizAnswers,
    previousQuestion,
    nextQuestion,
    resetQuiz,
  };

  return <ProductQuizContext.Provider value={value}>{children}</ProductQuizContext.Provider>;
};

export const useProductQuiz = (): ProductQuizContextType => {
  const context = useContext(ProductQuizContext);
  if (!context) {
    throw new Error('useProductQuiz must be used within a ProductQuizProvider');
  }
  return context;
};
